import { useCallback, useEffect, useState } from 'react';
import { getProjectRetention, retryProjectRetention, type RetentionSummary } from './api';

function jobLabel(status: NonNullable<RetentionSummary['job']>['status']): string {
  if (status === 'FAILED') return '削除処理エラー';
  if (status === 'COMPLETED') return '削除完了';
  if (status === 'CANCELLED') return '期限変更により取消';
  if (status === 'QUEUED') return '削除待ち';
  if (status === 'SCRUBBING_D1') return '台帳の最小化中';
  if (status === 'VERIFYING_R2') return '削除結果を確認中';
  return 'ファイル削除中';
}

export function RetentionPanel({
  canRetry,
  companyId,
  csrfToken,
  onError,
  projectId,
}: {
  canRetry: boolean;
  companyId: string;
  csrfToken?: string;
  onError: (error: unknown) => void;
  projectId: string;
}) {
  const [retention, setRetention] = useState<RetentionSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const value = await getProjectRetention(companyId, projectId);
    setRetention(value);
    return value;
  }, [companyId, projectId]);

  useEffect(() => {
    let active = true;
    void refresh()
      .then((value) => {
        if (active) setRetention(value);
      })
      .catch(onError);
    return () => {
      active = false;
    };
  }, [onError, refresh]);

  useEffect(() => {
    const status = retention?.job?.status;
    if (
      status !== 'QUEUED' &&
      status !== 'DELETING_R2' &&
      status !== 'VERIFYING_R2' &&
      status !== 'SCRUBBING_D1'
    ) {
      return;
    }
    const timer = window.setInterval(() => void refresh().catch(onError), 2500);
    return () => window.clearInterval(timer);
  }, [onError, refresh, retention?.job?.status]);

  async function retry() {
    if (csrfToken === undefined || retention?.job?.status !== 'FAILED') return;
    setBusy(true);
    try {
      setRetention(await retryProjectRetention(companyId, projectId, retention.job.id, csrfToken));
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  if (retention === null || retention.retentionExpiresOn === null) return null;
  const job = retention.job;
  return (
    <section className="retention-panel card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">DATA RETENTION</p>
          <h3>保存期限と一括保存</h3>
        </div>
        {canRetry && job?.status === 'FAILED' && (
          <button className="secondary" disabled={busy} onClick={() => void retry()} type="button">
            失敗した箇所から再開
          </button>
        )}
      </div>
      {retention.warning !== null && (
        <div
          className={`notice ${retention.warning.level === 'EXPIRED' ? 'error' : 'warning'}`}
          role="status"
        >
          <strong>{retention.warning.message}</strong>
          <span>下の「工事一括保存」で必要なZIPを保存してください。</span>
        </div>
      )}
      <dl className="retention-facts">
        <div>
          <dt>保存期限</dt>
          <dd>{retention.retentionExpiresOn}</dd>
        </div>
        <div>
          <dt>期限処理</dt>
          <dd>{job === null ? '未開始' : jobLabel(job.status)}</dd>
        </div>
        {job !== null && (
          <div>
            <dt>削除確認済み</dt>
            <dd>{job.deletedObjectCount.toLocaleString('ja-JP')}ファイル</dd>
          </div>
        )}
      </dl>
      {job?.errorDetail !== null && job?.errorDetail !== undefined && (
        <p className="error-message">{job.errorDetail}</p>
      )}
      <p className="muted">
        Google Slides原本は自動削除せず、One Colorの確認対象として記録します。
      </p>
    </section>
  );
}
